import { motion } from "framer-motion";
import { useContext, useState, useEffect } from "react";
import { Context } from "../../../Context";
import { FaEdit } from "react-icons/fa";
import { Modal } from "flowbite-react";
import { toast } from "react-toastify";
import { API, Storage } from "aws-amplify";

const VisitUs = () => {
  const { institutionData, setInstitutionData } = useContext(Context);
  const [isEditing, setIsEditing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [editData, setEditData] = useState({
    title: "Visit Us",
    address: "",
    phone: "",
    description: "Drop by anytime! We're open all week and ready to serve you with the finest tea experience.",
    map: ""
  });

  const UserCtx = useContext(Context);
  const isAdmin = UserCtx.userData.userType === "admin";

  // Initialize with context data
  useEffect(() => {
    if (institutionData) {
      setEditData({
        title: "Visit Us",
        address: institutionData.Query_Address || "",
        phone: institutionData.Query_PhoneNumber || "",
        description: institutionData.visitUs?.description || "Drop by anytime! We're open all week and ready to serve you with the finest tea experience.",
        map: institutionData.visitUs?.locationMap || ""
      });
    }
  }, [institutionData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async () => {
    try {
      setIsUploading(true);

      const updatedData = {
        ...institutionData,
        Query_Address: editData.address,
        Query_PhoneNumber: editData.phone,
        visitUs: {
          ...institutionData.visitUs,
          title: editData.title,
          description: editData.description,
          locationMap: editData.map
        }
      };

      setInstitutionData(updatedData);

      await API.put('main', `/admin/editable-ui/${institutionData.institution}?query=location`, {
        body: {
          Query_Address: editData.address,
          Query_PhoneNumber: editData.phone,
          visitUs: {
            title: editData.title,
            description: editData.description,
            locationMap: editData.map
          }
        }
      });

      toast.success("Changes saved successfully!", {
        position: "top-right",
        autoClose: 3000,
        style: {
          background: "#16A34A",
          color: "#ffffff",
          borderRadius: "8px",
          padding: "12px",
        },
      });

      setIsEditing(false);
    } catch (error) {
      console.error("Error saving changes:", error);
      toast.error("Failed to save changes. Please try again.", {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleCancel = () => {
    setEditData({
      title: "Visit Us",
      address: institutionData.Query_Address || "",
      phone: institutionData.Query_PhoneNumber || "",
      description: institutionData.visitUs?.description || "Drop by anytime! We're open all week and ready to serve you with the finest tea experience.",
      map: institutionData.visitUs?.locationMap || ""
    });
    toast.info("Changes discarded.", {
      position: "top-right",
      autoClose: 3000,
      style: {
        background: "#722F37",
        color: "#ffffff",
        borderRadius: "8px",
        padding: "12px",
      },
    });
    setIsEditing(false);
  };

  const isFormValid =
  editData.address.trim() !== "" &&
  /^\+\d{11,13}$/.test(editData.phone) &&
  editData.map.includes("google.com/maps/embed");


  return (
    <section className="flex flex-col md:flex-row items-center justify-center bg-secondaryColor py-16 px-8 font-lora relative">
      <div className="absolute top-4 right-4 z-20">
        {isAdmin && (
          <button
            onClick={() => setIsEditing(true)}
            className="bg-[var(--primary-color)] text-white px-4 py-2 rounded-full font-medium shadow-lg transition-all duration-300 flex items-center gap-2"
          >
            <FaEdit className="text-md" />
            <span className="text-md">Edit</span>
          </button>
        )}
      </div>
      {/* Map Section */}
      <motion.div
        className="w-full md:w-1/2 flex justify-center"
        initial={{ opacity: 0, x: -50 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 1, ease: "easeOut" }}
      >
        <div className="w-full max-w-lg h-[300px] md:h-[400px] lg:h-[450px] rounded-2xl overflow-hidden shadow-xl border-4 border-primaryColor/20 hover:shadow-2xl transition-all duration-300 ease-in-out hover:border-primaryColor/40">
          <iframe
            title="Location Map"
            src={editData.map}
            className="w-full h-full border-0 rounded-2xl"
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          ></iframe>
        </div>
      </motion.div>

      {/* Text Section */}
      <motion.div
        className="w-full md:w-1/2 p-8 text-center md:text-left mt-8 md:mt-0"
        initial={{ opacity: 0, x: 50 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 1, ease: "easeOut" }}
      >
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-primaryColor mb-6">
          {editData.title}
        </h2>

        <div className="space-y-6">
          <p className="text-primaryColor/80 text-lg sm:text-xl leading-relaxed">
            We're conveniently located at{" "}
            <span className="font-semibold text-primaryColor">
              {editData.address}
            </span>
          </p>

          <p className="text-primaryColor/80 text-lg sm:text-xl leading-relaxed">
            For inquiries, call us at{" "}
            <a href={`tel:${editData.phone}`} className="font-semibold text-primaryColor">
              {editData.phone}
            </a>
          </p>

          <p className="text-primaryColor/80 text-lg sm:text-xl leading-relaxed">
            {editData.description}
          </p>
        </div>

        {/* Get Directions button */}
        <motion.a
          href={`https://maps.google.com/?q=${editData.address}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block mt-8 px-8 py-3 bg-primaryColor text-white rounded-full font-medium hover:bg-primaryColor/90 transition-colors duration-300 shadow-lg hover:shadow-xl"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          Get Directions
        </motion.a>
      </motion.div>

      {/* Flowbite Modal */}
      <Modal show={isEditing} onClose={handleCancel} size="md">
        <Modal.Header className="border-b p-4">
          Edit Location Details
        </Modal.Header>
        <Modal.Body className="p-4 space-y-4">
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Business Address <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="address"
                value={editData.address}
                onChange={handleInputChange}
                maxLength={50}
                className="w-full p-2 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                disabled={isUploading}
              />
              <p className="text-xs text-gray-500 mt-1">
                {editData.address.length}/50 characters
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone Number <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                name="phone"
                value={editData.phone}
                onChange={(e) => {
                  const value = e.target.value;
                  // Allow only "+" at start and digits after, up to 14 characters total
                  if (/^\+?\d*$/.test(value) && value.length <= 14) {
                    handleInputChange({
                      target: {
                        name: "phone",
                        value: value,
                      },
                    });
                  }
                }}
                pattern="^\+\d{11,13}$"
                className="w-full p-2 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                placeholder="e.g. +911234567890"
                disabled={isUploading}
              />
              {editData.phone && !/^\+\d{11,13}$/.test(editData.phone) && (
                <p className="text-xs text-red-500 mt-1">
                  Enter a valid phone number starting with ‘+’, followed by a country code (1–3 digits) and a 10-digit mobile number.<br />
                  Examples: <code>+11234567890</code>, <code>+911234567890</code>, <code>+8801234567890</code>
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Map Embed URL <span className="text-red-500">*</span>
              </label>
              <input
                type="url"
                name="map"
                value={editData.map}
                onChange={handleInputChange}
                className="w-full p-2 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                placeholder="https://www.google.com/maps/embed?pb=..."
                disabled={isUploading}
              />
              {editData.map && !editData.map.includes('google.com/maps/embed') && (
                <p className="text-xs text-red-500 mt-1">
                  Please use a valid Google Maps embed URL
                </p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                Tip: In Google Maps, click "Share" → "Embed a map" → Copy the iframe src URL
              </p>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="border-t p-4">
          <div className="w-full flex justify-center gap-2">
            <button
              onClick={handleSave}
              disabled={isUploading || !isFormValid}
              className={`text-white ${isUploading || !isFormValid
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-primaryColor'
                } focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center dark:focus:ring-blue-800`}
            >
              {isUploading ? "Saving..." : "Save"}
            </button>
          </div>
        </Modal.Footer>
      </Modal>
    </section>
  );
};

export default VisitUs;